import React, { useState, useRef, useEffect } from "react";
import { Settings, Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import dayjs from "dayjs";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

// FREE STACK ONLY (no MUI Pro / license needed):
//   npm install dayjs @mui/material @mui/icons-material @emotion/react @emotion/styled

export const PERIODS = [
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
  { key: "quarterly", label: "Quarterly" },
  { key: "annually", label: "Annually" },
];

const SHORTCUTS = [
  {
    label: "This week",
    getRange: () => {
      const t = dayjs();
      return [t.startOf("week"), t.endOf("week")];
    },
  },
  {
    label: "Last week",
    getRange: () => {
      const t = dayjs().subtract(7, "day");
      return [t.startOf("week"), t.endOf("week")];
    },
  },
  {
    label: "Last 7 days",
    getRange: () => {
      const t = dayjs();
      return [t.subtract(6, "day"), t];
    },
  },
  {
    label: "This month",
    getRange: () => {
      const t = dayjs();
      return [t.startOf("month"), t.endOf("month")];
    },
  },
  {
    label: "This year",
    getRange: () => {
      const t = dayjs();
      return [t.startOf("year"), t.endOf("year")];
    },
  },
];

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function buildMonthGrid(monthDate) {
  const start = monthDate.startOf("month").startOf("week");
  const end = monthDate.endOf("month").endOf("week");
  const days = [];
  let cur = start;
  while (cur.isBefore(end) || cur.isSame(end, "day")) {
    days.push(cur);
    cur = cur.add(1, "day");
  }
  return days;
}

function MonthGrid({ monthDate, onNavigate, showPrevArrow, showNextArrow, range, hoverDate, onDayClick, onDayHover }) {
  const days = buildMonthGrid(monthDate);
  const [rangeStart, rangeEnd] = range;

  const isInRange = (d) => {
    if (!rangeStart) return false;
    const end = rangeEnd || hoverDate;
    if (!end) return false;
    const lo = rangeStart.isBefore(end) ? rangeStart : end;
    const hi = rangeStart.isBefore(end) ? end : rangeStart;
    return d.isAfter(lo, "day") && d.isBefore(hi, "day");
  };

  const isEndpoint = (d) => (rangeStart && d.isSame(rangeStart, "day")) || (rangeEnd && d.isSame(rangeEnd, "day"));

  return (
    <div className="w-[280px]">
      <div className="flex items-center justify-between px-2 mb-2">
        <button onClick={() => showPrevArrow && onNavigate(-1)} className={`p-1 rounded hover:bg-gray-100 ${!showPrevArrow ? "invisible" : ""}`}>
          <ChevronLeft size={16} className="text-gray-400" />
        </button>
        <span className="text-sm font-medium text-gray-800">{monthDate.format("MMM YYYY")}</span>
        <button onClick={() => showNextArrow && onNavigate(1)} className={`p-1 rounded hover:bg-gray-100 ${!showNextArrow ? "invisible" : ""}`}>
          <ChevronRight size={16} className="text-gray-400" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-y-1 px-2">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-center text-[11px] text-gray-400 font-medium py-1">
            {w}
          </div>
        ))}

        {days.map((d) => {
          const outside = !d.isSame(monthDate, "month");
          const inRange = isInRange(d);
          const endpoint = isEndpoint(d);

          return (
            <div key={d.format("YYYY-MM-DD")} className="flex items-center justify-center relative">
              {inRange && <div className="absolute inset-y-0 left-0 right-0 bg-sky-50" />}
              <button
                onClick={() => onDayClick(d)}
                onMouseEnter={() => onDayHover(d)}
                className={`relative z-10 w-7 h-7 text-xs rounded-full transition-colors
                  ${outside ? "text-gray-300" : "text-gray-700"}
                  ${endpoint ? "bg-blue-600 text-white font-medium" : ""}
                  ${!endpoint && !outside ? "hover:bg-sky-100" : ""}
                `}
              >
                {d.date()}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DateRangeField({ from, to, onCustomRangeChange }) {
  const [open, setOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(dayjs().startOf("month"));
  const [rangeStart, setRangeStart] = useState(from ? dayjs(from) : null);
  const [rangeEnd, setRangeEnd] = useState(to ? dayjs(to) : null);
  const [hoverDate, setHoverDate] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setRangeStart(from ? dayjs(from) : null);
    setRangeEnd(to ? dayjs(to) : null);
  }, [from, to]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDayClick = (d) => {
    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(d);
      setRangeEnd(null);
    } else {
      if (d.isBefore(rangeStart, "day")) {
        setRangeEnd(rangeStart);
        setRangeStart(d);
      } else {
        setRangeEnd(d);
      }
    }
  };

  const applyShortcut = (getRange) => {
    const [s, e] = getRange();
    setRangeStart(s);
    setRangeEnd(e);
    setVisibleMonth(s.startOf("month"));
  };

  const handleConfirm = () => {
    onCustomRangeChange({
      from: rangeStart ? rangeStart.format("YYYY-MM-DD") : "",
      to: rangeEnd ? rangeEnd.format("YYYY-MM-DD") : "",
    });
    setOpen(false);
  };

  const secondMonth = visibleMonth.add(1, "month");
  const displayFrom = from || "Start date";
  const displayTo = to || "End date";

  return (
    <div className="relative" ref={wrapperRef}>
      <button onClick={() => setOpen((v) => !v)} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs border transition-colors ${open ? "border-sky-400" : "border-gray-200 hover:bg-gray-50"}`}>
        <span className="text-gray-700">{displayFrom}</span>
        <span className="text-gray-400">→</span>
        <span className="text-gray-700">{displayTo}</span>
        <CalendarIcon size={14} className="text-gray-400" />
      </button>

      {open && (
        <div className="absolute z-20 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          <div className="flex">
            <MonthGrid monthDate={visibleMonth} onNavigate={(dir) => setVisibleMonth((m) => m.add(dir, "month"))} showPrevArrow showNextArrow={false} range={[rangeStart, rangeEnd]} hoverDate={hoverDate} onDayClick={handleDayClick} onDayHover={setHoverDate} />
            <div className="w-px bg-gray-100" />
            <MonthGrid monthDate={secondMonth} onNavigate={(dir) => setVisibleMonth((m) => m.add(dir, "month"))} showPrevArrow={false} showNextArrow range={[rangeStart, rangeEnd]} hoverDate={hoverDate} onDayClick={handleDayClick} onDayHover={setHoverDate} />
          </div>

          <div className="flex flex-wrap gap-1.5 px-3 py-2 border-t border-gray-100">
            {SHORTCUTS.map((s) => (
              <button key={s.label} onClick={() => applyShortcut(s.getRange)} className="px-2.5 py-1 text-xs rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50">
                {s.label}
              </button>
            ))}
          </div>

          <div className="flex justify-end gap-2 px-3 py-2 border-t border-gray-100">
            <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-xs rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={handleConfirm} className="px-3 py-1.5 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700">
              Confirm
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const uncheckedIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

function staffLabel(user) {
  return [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email || `User ${user.id}`;
}

function StaffMultiSelect({ staffUsers, selectedStaffIds, onStaffChange }) {
  const selectedUsers = staffUsers.filter((u) => selectedStaffIds.includes(u.id));

  return (
    <Autocomplete
      multiple
      size="small"
      options={staffUsers}
      value={selectedUsers}
      onChange={(event, newValue) => onStaffChange(newValue.map((u) => u.id))}
      getOptionLabel={staffLabel}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      disableCloseOnSelect
      limitTags={2}
      sx={{
        minWidth: 200,
        maxWidth: 280,
        "& .MuiOutlinedInput-root": {
          padding: "6px 8px !important",
          minHeight: "auto",
          borderRadius: "6px",
          backgroundColor: "#fff",
          transition: "background-color 150ms",
        },
        "& .MuiOutlinedInput-root:hover": {
          backgroundColor: "#f9fafb",
        },
        "& .MuiOutlinedInput-root.Mui-focused": {
          backgroundColor: "#fff",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#e5e7eb",
          borderWidth: "1px",
        },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "#e5e7eb",
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "#7dd3fc",
          borderWidth: "1px",
        },
        "& .MuiAutocomplete-input": {
          fontSize: "12px",
          padding: "0 4px !important",
        },
        "& .MuiAutocomplete-tag": {
          margin: "2px",
        },
      }}
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option.id} style={{ fontSize: "12px", minHeight: "auto", padding: "4px 10px" }}>
          <Checkbox icon={uncheckedIcon} checkedIcon={checkedIcon} style={{ marginRight: 6, padding: 4 }} checked={selected} size="small" />
          {staffLabel(option)}
        </li>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={staffLabel(option)}
            size="small"
            sx={{
              height: 16,
              fontSize: "12px",
              borderRadius: "4px",
              backgroundColor: "#f3f4f6",
              color: "#4b5563",
              "& .MuiChip-label": { padding: "0 5px" },
              "& .MuiChip-deleteIcon": { fontSize: "13px", margin: "0 2px 0 -2px", color: "#9ca3af" },
            }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={selectedUsers.length ? "" : "Staff"}
          sx={{
            "& .MuiInputBase-root": { fontSize: "12px", color: "#374151" },
            "& .MuiInputBase-input::placeholder": { color: "#6b7280", opacity: 1 },
          }}
        />
      )}
    />
  );
}

export default function FilterBar({ period, onPeriodChange, from, to, onCustomRangeChange, onManageStages, staffUsers = [], selectedStaffIds = [], onStaffChange }) {
  return (
    <div className="flex items-center gap-2 flex-wrap bg-white border border-gray-100 rounded-xl shadow-sm px-3 py-2 mb-4">
      <span className="text-xs text-gray-500 mr-1">Period</span>

      {PERIODS.map((p) => (
        <button key={p.key} onClick={() => onPeriodChange(p.key)} className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${period === p.key ? "border-sky-400 bg-sky-50 text-sky-700" : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"}`}>
          {p.label}
        </button>
      ))}

      <span className="w-px h-5 bg-gray-200 mx-1" />

      <DateRangeField from={from} to={to} onCustomRangeChange={onCustomRangeChange} />

      <span className="w-px h-5 bg-gray-200 mx-1" />

      <StaffMultiSelect staffUsers={staffUsers} selectedStaffIds={selectedStaffIds} onStaffChange={onStaffChange} />

      <button onClick={onManageStages} className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-gray-200 text-gray-500 hover:bg-gray-50">
        <Settings size={13} />
        <span>Manage stages</span>
      </button>
    </div>
  );
}
